import { Box, chakra, Flex, Grid, Stack, type FlexProps } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import dove from '~/assets/img/photos/dove.webp'
import { ROOMY } from '~/theme/media'

const Img = chakra('img')
const Svg = chakra('svg')

// From `lg` (and not a phone in landscape): the showcase beside the card.
const SPLIT = '@media screen and (min-width: 64rem) and (min-height: 31.25rem)'

/**
 * The split card of vocdoni-app's sign-in (`product-auth`), for the start page and the log-in page (design spec
 * 2026-10-30 §2.4): from `lg` the form column left and the showcase (the sky and the dove) right. Below `lg` the
 * showcase is left out and the card is the same phone-width card as every other member screen; on a phone it has no
 * frame and the content sits on the canvas of the app shell.
 */
export function SplitCard({ children, showcase }: { children: ReactNode; showcase: ReactNode }) {
  return (
    <Grid
      templateColumns={{ base: 'minmax(0, 1fr)', lg: '46fr 54fr' }}
      borderRadius='lg'
      overflow='hidden'
      w='full'
      mx='auto'
      css={{
        // A content surface raised off the canvas (R113), phone-width like the other member cards.
        [ROOMY]: {
          maxWidth: '30rem',
          borderWidth: '1px',
          borderColor: 'border',
          background: 'bg',
          boxShadow: 'surface',
        },
        [SPLIT]: { maxWidth: '60rem' },
      }}
    >
      <Stack
        justify='center'
        minW={0}
        css={{ gap: '4', [ROOMY]: { gap: '5', padding: '8' }, [SPLIT]: { padding: '12' } }}
      >
        {children}
      </Stack>
      <Box display={{ base: 'none', lg: 'block' }} position='relative' minH='34rem' minW={0}>
        {showcase}
      </Box>
    </Grid>
  )
}

// Concentric pastel arcs, very faint; same hues in both modes.
const RAINBOW = ['#e8a0a0', '#e8c48c', '#e8e08c', '#a0d4a8', '#8cb8e0', '#b4a0d8'] as const

/**
 * The calm sky behind a showcase panel: a gradient from the deco blue tint to the page colour (light and dark follow
 * the theme tokens), faint concentric rainbow arcs and the transparent white dove. Everything is bundled and static;
 * the dove is decorative (`alt=''`) with its size set so nothing moves while it loads.
 */
export function SkyScene({ children, doveWidth = 220, ...rest }: { doveWidth?: number } & FlexProps) {
  // The arcs are drawn around the dove itself (not across the whole panel), so they stay centred on it and are never
  // cut by the panel edge, whatever the panel's shape. The top padding leaves room for the outer arc.
  const ring = Math.round(doveWidth * 1.8)
  return (
    <Flex
      data-sky
      position='absolute'
      inset={0}
      direction='column'
      overflow='hidden'
      bgGradient='to-b'
      gradientFrom='deco.blue.bg'
      gradientTo='bg'
      {...rest}
    >
      <Flex position='relative' justify='center' pb={6} style={{ paddingTop: `${Math.round(32 + doveWidth * 0.3)}px` }}>
        <Box position='relative' w={`${doveWidth}px`}>
          <Svg
            aria-hidden
            focusable='false'
            viewBox='0 0 100 100'
            position='absolute'
            left='50%'
            top='50%'
            w={`${ring}px`}
            h={`${ring}px`}
            transform='translate(-50%, -50%)'
          >
            {RAINBOW.map((color, i) => (
              <circle
                key={color}
                cx='50'
                cy='50'
                r={48 - i * 4.2}
                fill='none'
                stroke={color}
                strokeWidth='2.3'
                opacity='0.2'
              />
            ))}
          </Svg>
          <Img
            src={dove}
            alt=''
            loading='eager'
            decoding='async'
            htmlWidth={490}
            htmlHeight={605}
            position='relative'
            display='block'
            w={`${doveWidth}px`}
            h='auto'
            transform='rotate(-5deg)'
            // The cut-out is white: a soft shadow keeps its edge readable on the pale sky.
            filter='drop-shadow(0 6px 14px rgba(60, 90, 140, 0.35))'
          />
        </Box>
      </Flex>
      {children}
    </Flex>
  )
}
